import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useOutletContext, Link, useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import parseSimplifiedUserIdentifier from "../../common/util/parseSimplifiedUserIdentifier";
import {
  SessionCard,
  SearchAndSort,
  SortOption,
  EmptyState,
  ExportButton,
  Pagination,
  ScoreGaugeCard,
  EngagementCard,
  TopInteractionsCard,
} from "../../features/statsNew/components";
import { useModuleStats, useGazeHeatmapCheck, useGraphStats } from "../../features/statsNew/hooks";
import { exportSessionsToCsv } from "../../features/statsNew/utils/exportCsv";
import { getSessionDate, getSessionTimestamp } from "../../features/statsNew/utils/dateUtils";
import { ModuleViewSkeleton } from "./components";
import type { DateRange } from "../../features/statsNew/components";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";
import {
  SuccessRatePerSceneChart,
  InteractionAnalysisTable,
  StatsGraph,
} from "../../features/statsNew/components/charts";
import {
  getAllSessionsByProjectGroup,
  getSelectedProjectGroupWithSessions,
  getSelectedProjectWithSessions,
  getScorePerSceneForProject,
  getSelectedProjectScorePerScene,
  sessionsByProjectGroupSelector,
  setSelectedProjectScorePerScene,
} from "../../features/stats/state/statsSlice";
import { AppDispatch, RootState } from "../../common/state/store";
import SessionDetailModal from "../../features/statsNew/components/modals/SessionDetailModal";
import { InteractionAttemptsModal } from "../../features/statsNew/components/modals";

interface OutletContextType {
  dateRange: DateRange;
}

type TabKey = "stats" | "sessions" | "apprenants";

const ModuleView = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const locale = i18n.language === "fr" ? fr : enUS;
  const { parcoursId, moduleId, userId } = useParams<{
    parcoursId: string;
    moduleId: string;
    userId?: string;
  }>();
  const { dateRange } = useOutletContext<OutletContextType>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we're in learner context
  const isLearnerContext = !!userId;
  const isAnonymousView = userId === "anonymous";

  // Read sessionId from URL query parameter
  const sessionIdFromUrl = searchParams.get("sessionId");

  const [activeTab, setActiveTab] = useState<TabKey>(sessionIdFromUrl ? "sessions" : "stats");
  const [searchValue, setSearchValue] = useState("");
  const [sortKey, setSortKey] = useState("date-desc");
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    sessionIdFromUrl ? Number(sessionIdFromUrl) : null,
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isScoreDataLoading, setIsScoreDataLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [apprenantsSearchValue, setApprenantsSearchValue] = useState("");
  const [apprenantsSortKey, setApprenantsSortKey] = useState("sessions-desc");
  const [apprenantsCurrentPage, setApprenantsCurrentPage] = useState(0);
  const [apprenantsPageSize, setApprenantsPageSize] = useState(25);
  const [selectedSceneId, setSelectedSceneId] = useState<number | string | null>(null);
  const [selectedSceneName, setSelectedSceneName] = useState<string | null>(null);
  const [selectedInteraction, setSelectedInteraction] = useState<{
    contentId: number;
    contentName: string;
    contentType: string;
  } | null>(null);
  const fetchingModuleIdRef = useRef<string | null>(null);
  const sessionsFetchedRef = useRef(false);
  // Track if scene selection came from the graph (to avoid auto-focus when clicking on graph)
  const sceneSelectedFromGraphRef = useRef(false);

  const parcours = useSelector((state: RootState) =>
    getSelectedProjectGroupWithSessions(Number(parcoursId))(state),
  );
  const project = useSelector((state: RootState) =>
    getSelectedProjectWithSessions(Number(moduleId))(state),
  );
  const scorePerScene = useSelector(getSelectedProjectScorePerScene);
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  // Fetch graph stats for the module structure visualization
  const graphStats = useGraphStats({
    projectId: Number(moduleId),
    startDate: dateRange.startDate?.toISOString(),
    endDate: dateRange.endDate?.toISOString(),
  });

  // Extract interaction stats for TopInteractionsCard
  const topInteractionsData = useMemo(() => {
    if (!graphStats.nodes || graphStats.nodes.length === 0) return [];

    // Filter assets (interactions) that have stats data
    // Stats are only present when there are actual attempts
    return graphStats.nodes
      .filter(
        (node): node is typeof node & { stats: NonNullable<typeof node.stats> } =>
          node.type === "asset" && node.stats != null,
      )
      .map((node) => ({
        contentId: node.contentId,
        contentName: node.name,
        successRate: node.stats.successRate,
        attemptCount: node.stats.attemptCount,
      }));
  }, [graphStats.nodes]);

  // Get learner info when in learner context
  const learnerInfo = useMemo(() => {
    if (!isLearnerContext) return null;

    // For anonymous view, return translated "Anonymous" name
    if (isAnonymousView) {
      return { displayName: t("statsNew.apprenants.anonymous") };
    }

    for (const group of sessionsByProjectGroup) {
      for (const proj of group.projects) {
        const session = proj.sessions.find((s) => s.enduser?.id === Number(userId));
        if (session?.enduser) {
          const { firstname, lastname, username } = session.enduser;
          const displayName =
            firstname || lastname ? `${firstname || ""} ${lastname || ""}`.trim() : username || "";
          return { displayName };
        }
      }
    }
    return null;
  }, [sessionsByProjectGroup, userId, isLearnerContext, isAnonymousView, t]);

  useEffect(() => {
    // Prevent duplicate fetches (handles StrictMode and re-renders)
    if (sessionsFetchedRef.current) return;
    sessionsFetchedRef.current = true;

    dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewModuleView" }));
  }, [dispatch]);

  useEffect(() => {
    if (!moduleId) return undefined;

    // Create a unique key for this fetch (moduleId + date range)
    const fetchKey = `${moduleId}-${dateRange.startDate?.toISOString() || "null"}-${
      dateRange.endDate?.toISOString() || "null"
    }`;

    // Prevent duplicate fetches for the same key (handles StrictMode and re-renders)
    if (fetchingModuleIdRef.current === fetchKey) {
      return undefined;
    }

    // Mark this key as being fetched
    fetchingModuleIdRef.current = fetchKey;

    // Clear old scene data and show loading state
    dispatch(setSelectedProjectScorePerScene([]));
    setIsScoreDataLoading(true);

    // Fetch new data with date range
    const promise = dispatch(
      getScorePerSceneForProject({
        projectId: Number(moduleId),
        componentId: "statsNewModuleView",
        startDate: dateRange.startDate?.toISOString(),
        endDate: dateRange.endDate?.toISOString(),
      }),
    );

    // Stop loading when the promise resolves (success or error)
    promise
      .unwrap()
      .then(() => {
        // Only update state if still on the same fetch
        if (fetchingModuleIdRef.current === fetchKey) {
          setIsScoreDataLoading(false);
        }
      })
      .catch(() => {
        // Only update state if still on the same fetch (handles navigation away)
        if (fetchingModuleIdRef.current === fetchKey) {
          setIsScoreDataLoading(false);
        }
      });

    // No cleanup needed - let the fetch complete
    // The ref check prevents duplicate fetches
    return undefined;
  }, [dispatch, moduleId, dateRange]);

  // Track when data has loaded at least once
  useEffect(() => {
    if (project?.project_id && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [project, isInitialLoad]);

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  const sortOptions: SortOption[] = [
    { key: "date-desc", label: t("statsNew.sort.activityRecent") },
    { key: "date-asc", label: t("statsNew.sort.activityOldest") },
    { key: "score-desc", label: t("statsNew.sort.scoreHigh") },
    { key: "score-asc", label: t("statsNew.sort.scoreLow") },
  ];

  // Session type for filtering
  interface FilterableSession {
    is_completed?: boolean;
    is_succeeded?: boolean;
  }

  // Filter chips configuration
  type FilterChip = {
    id: string;
    label: string;
    filter: (session: FilterableSession) => boolean;
  };

  const filterChips: FilterChip[] = [
    {
      id: "completed",
      label: t("statsNew.filters.completed"),
      filter: (s) => s.is_completed === true,
    },
    {
      id: "failed",
      label: t("statsNew.filters.failed"),
      filter: (s) => s.is_succeeded === false,
    },
  ];

  const toggleFilter = useCallback((filterId: string) => {
    setActiveFilters((prev) =>
      prev.includes(filterId) ? prev.filter((f) => f !== filterId) : [...prev, filterId],
    );
    setCurrentPage(0); // Reset to first page when filter changes
  }, []);

  // Filter sessions by date range and user (in learner context)
  const filteredSessions = useMemo(() => {
    if (!project?.sessions) return [];

    return project.sessions.filter((session) => {
      // Filter by user in learner context
      if (isLearnerContext) {
        if (isAnonymousView) {
          // For anonymous view, only include sessions without enduser
          if (session.enduser) return false;
        } else {
          // For specific user view, filter by user ID
          if (session.enduser?.id !== Number(userId)) return false;
        }
      }

      const sessionDate = getSessionDate(session);
      if (dateRange.startDate && sessionDate < dateRange.startDate) return false;
      if (dateRange.endDate && sessionDate > dateRange.endDate) return false;
      return true;
    });
  }, [project, dateRange, isLearnerContext, isAnonymousView, userId]);

  // Compute module stats using custom hook
  const moduleStats = useModuleStats(filteredSessions);

  // Check which sessions have gaze heatmap data available
  const sessionIdsForGazeCheck = useMemo(
    () => filteredSessions.map((s) => s.id),
    [filteredSessions],
  );
  const { gazeDataAvailability } = useGazeHeatmapCheck(sessionIdsForGazeCheck);

  // Compute unique learners from filtered sessions
  interface LearnerData {
    id: number | string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    sessionCount: number;
    lastSessionDate: Date | null;
    lastScore: number | null;
    bestScore: number | null;
    avatarUrl?: string;
    isAnonymous: boolean;
  }

  const uniqueLearnersData = useMemo((): LearnerData[] => {
    const learnerMap = new Map<string, LearnerData>();

    filteredSessions.forEach((session) => {
      const isAnonymous = !session.enduser;
      const uniqueKey = isAnonymous ? "anon_all" : `user_${session.enduser?.id}`;
      const sessionDate = getSessionDate(session);

      const existing = learnerMap.get(uniqueKey);

      if (existing) {
        existing.sessionCount++;
        if (!existing.lastSessionDate || sessionDate > existing.lastSessionDate) {
          existing.lastSessionDate = sessionDate;
          existing.lastScore = session.score ?? null;
        }
        if (session.score != null) {
          if (existing.bestScore == null || session.score > existing.bestScore) {
            existing.bestScore = session.score;
          }
        }
      } else {
        learnerMap.set(uniqueKey, {
          id: isAnonymous ? "anonymous" : session.enduser?.id || 0,
          firstName: session.enduser?.firstname || "",
          lastName: session.enduser?.lastname || "",
          email: session.enduser?.email || "",
          username: session.user_name || session.enduser?.username || "",
          sessionCount: 1,
          lastSessionDate: sessionDate,
          lastScore: session.score ?? null,
          bestScore: session.score ?? null,
          avatarUrl: session.enduser?.source_avatar?.s3_url,
          isAnonymous,
        });
      }
    });

    return Array.from(learnerMap.values());
  }, [filteredSessions]);

  // Sort options for apprenants tab
  const apprenantsSortOptions: SortOption[] = [
    { key: "sessions-desc", label: t("statsNew.sort.sessionsMost") },
    { key: "sessions-asc", label: t("statsNew.sort.sessionsLeast") },
    { key: "score-desc", label: t("statsNew.sort.scoreHigh") },
    { key: "score-asc", label: t("statsNew.sort.scoreLow") },
    { key: "date-desc", label: t("statsNew.sort.activityRecent") },
    { key: "date-asc", label: t("statsNew.sort.activityOldest") },
  ];

  // Filter and sort learners
  const displayLearners = useMemo(() => {
    let result = uniqueLearnersData;

    // Search filter
    if (apprenantsSearchValue) {
      const search = apprenantsSearchValue.toLowerCase();
      result = result.filter(
        (l) =>
          l.firstName.toLowerCase().includes(search) ||
          l.lastName.toLowerCase().includes(search) ||
          l.email.toLowerCase().includes(search) ||
          l.username.toLowerCase().includes(search),
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (apprenantsSortKey) {
        case "sessions-desc":
          return b.sessionCount - a.sessionCount;
        case "sessions-asc":
          return a.sessionCount - b.sessionCount;
        case "score-desc":
          return (b.bestScore || 0) - (a.bestScore || 0);
        case "score-asc":
          return (a.bestScore || 0) - (b.bestScore || 0);
        case "date-desc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return b.lastSessionDate.getTime() - a.lastSessionDate.getTime();
        case "date-asc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return a.lastSessionDate.getTime() - b.lastSessionDate.getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [uniqueLearnersData, apprenantsSearchValue, apprenantsSortKey]);

  // Paginate learners
  const paginatedLearners = useMemo(() => {
    const start = apprenantsCurrentPage * apprenantsPageSize;
    return displayLearners.slice(start, start + apprenantsPageSize);
  }, [displayLearners, apprenantsCurrentPage, apprenantsPageSize]);

  // Reset apprenants page when search changes
  useEffect(() => {
    setApprenantsCurrentPage(0);
  }, [apprenantsSearchValue, apprenantsSortKey]);

  // Filter and sort sessions for the sessions tab
  const displaySessions = useMemo(() => {
    let result = filteredSessions;

    // Search filter (by user name if available)
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((s) => {
        const userName = s.user_name || s.enduser?.username || "";
        return userName.toLowerCase().includes(search);
      });
    }

    // Apply active filter chips (cumulative - session must pass all active filters)
    if (activeFilters.length > 0) {
      result = result.filter((session) =>
        activeFilters.every((filterId) => {
          const chip = filterChips.find((c) => c.id === filterId);
          return chip ? chip.filter(session) : true;
        }),
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "date-desc":
          return getSessionTimestamp(b) - getSessionTimestamp(a);
        case "date-asc":
          return getSessionTimestamp(a) - getSessionTimestamp(b);
        case "score-desc":
          return (b.score || 0) - (a.score || 0);
        case "score-asc":
          return (a.score || 0) - (b.score || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [filteredSessions, searchValue, sortKey, activeFilters, filterChips]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, sortKey, dateRange]);

  // Paginate the sessions
  const paginatedSessions = useMemo(() => {
    const start = currentPage * pageSize;
    return displaySessions.slice(start, start + pageSize);
  }, [displaySessions, currentPage, pageSize]);

  // Handle page size change - reset to first page
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleExport = async () => {
    const locale = t("statsNew.title") === "Statistiques" ? "fr" : "en";
    const moduleName = project?.project_name?.replace(/[^a-zA-Z0-9]/g, "-") || "module";
    await exportSessionsToCsv(displaySessions, {
      filename: `sessions-${moduleName}`,
      locale,
    });
  };

  // Show skeleton during initial load
  if (isInitialLoad && !project?.project_id) {
    return <ModuleViewSkeleton />;
  }

  if (!project?.project_id) {
    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.module.notFound")}
        description={t("statsNew.emptyState.noModulesDescription")}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-4">
        {isLearnerContext ? (
          <>
            <Link to="/statistics/apprenants">{t("statsNew.tabs.apprenants")}</Link>
            <span className="stats-breadcrumb-separator">/</span>
            <Link to={`/statistics/apprenants/${userId}`}>
              {learnerInfo?.displayName || userId}
            </Link>
            <span className="stats-breadcrumb-separator">/</span>
            <Link to={`/statistics/apprenants/${userId}/${parcoursId}`}>
              {parcours?.project_group_name}
            </Link>
            <span className="stats-breadcrumb-separator">/</span>
            <span className="stats-breadcrumb-current">{project.project_name}</span>
          </>
        ) : (
          <>
            <Link to="/statistics/parcours">{t("statsNew.tabs.parcours")}</Link>
            <span className="stats-breadcrumb-separator">/</span>
            <Link to={`/statistics/parcours/${parcoursId}`}>{parcours?.project_group_name}</Link>
            <span className="stats-breadcrumb-separator">/</span>
            <span className="stats-breadcrumb-current">{project.project_name}</span>
          </>
        )}
      </nav>

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">{project.project_name}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {moduleStats.totalSessions} {t("statsNew.session.sessions")}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab("stats")}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === "stats" ? "text-green-600" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("statsNew.module.statsTab")}
          {activeTab === "stats" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("sessions")}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === "sessions" ? "text-green-600" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("statsNew.module.sessionsTab")}
          {activeTab === "sessions" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
          )}
        </button>
        {!isLearnerContext && (
          <button
            onClick={() => setActiveTab("apprenants")}
            className={`px-6 py-3 text-sm font-medium transition-colors relative ${
              activeTab === "apprenants" ? "text-green-600" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t("statsNew.module.apprenantsTab")}
            {activeTab === "apprenants" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
            )}
          </button>
        )}
      </div>

      {/* Tab content */}
      {activeTab === "stats" && (
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Zone 1: Hero Metrics - 2 grandes cartes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ScoreGaugeCard
              avgScore={moduleStats.avgScore}
              maxScore={moduleStats.avgMaxScore ?? undefined}
              bestScore={moduleStats.bestScore}
              worstScore={moduleStats.worstScore}
            />
            <EngagementCard
              totalSessions={moduleStats.totalSessions}
              completionRate={moduleStats.completionRate}
              avgProgressionPercent={moduleStats.avgProgressionPercent}
              avgDuration={moduleStats.avgDuration}
              abandonedSessions={moduleStats.abandonedSessions}
            />
          </div>

          {/* Zone 2: Module Structure Graph - Toujours visible */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-500">{t("statsNew.graph.title")}</h3>
              <p className="text-xs text-gray-400">{t("statsNew.charts.clickToFilter")}</p>
            </div>
            <StatsGraph
              nodes={graphStats.nodes}
              links={graphStats.links}
              layout={graphStats.layout}
              isLoading={graphStats.isLoading}
              error={graphStats.error}
              height={400}
              focusNodeId={
                sceneSelectedFromGraphRef.current
                  ? null
                  : selectedSceneId != null
                  ? Number(selectedSceneId)
                  : null
              }
              onSceneClick={(node) => {
                sceneSelectedFromGraphRef.current = true;
                if (selectedSceneId === node.contentId) {
                  setSelectedSceneId(null);
                  setSelectedSceneName(null);
                } else {
                  setSelectedSceneId(node.contentId);
                  setSelectedSceneName(node.name);
                }
              }}
              onInteractionClick={(node) => {
                setSelectedInteraction({
                  contentId: node.contentId,
                  contentName: node.name,
                  contentType: node.modelName,
                });
              }}
            />
          </div>

          {/* Zone 3: Analyse détaillée - 2 colonnes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Performance par scène */}
            <div className="lg:col-span-2 stats-chart-container">
              <h3 className="text-sm font-medium text-gray-500">
                {t("statsNew.charts.successRatePerScene")}
              </h3>
              <p className="text-xs text-gray-400 mb-2">{t("statsNew.charts.clickToFilter")}</p>
              <SuccessRatePerSceneChart
                data={scorePerScene}
                isLoading={isScoreDataLoading}
                selectedSceneId={selectedSceneId}
                onSceneClick={(contentId, sceneName) => {
                  sceneSelectedFromGraphRef.current = false;
                  // contentId from API is now the same as contentId used in graph
                  if (selectedSceneId === contentId) {
                    setSelectedSceneId(null);
                    setSelectedSceneName(null);
                  } else {
                    setSelectedSceneId(contentId);
                    setSelectedSceneName(sceneName);
                  }
                }}
              />
            </div>

            {/* Top Interactions - Quick insights */}
            <TopInteractionsCard
              interactions={topInteractionsData}
              isLoading={graphStats.isLoading}
              onInteractionClick={(contentId, contentName) => {
                // Find the interaction type from graph nodes
                const node = graphStats.nodes.find((n) => n.contentId === contentId);
                setSelectedInteraction({
                  contentId,
                  contentName,
                  contentType: node?.modelName || "",
                });
              }}
            />
          </div>

          {/* Tableau d'analyse des interactions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("statsNew.interactions.title")}
            </h3>
            <InteractionAnalysisTable
              projectId={Number(moduleId)}
              startDate={dateRange.startDate?.toISOString()}
              endDate={dateRange.endDate?.toISOString()}
              filterSceneId={selectedSceneId}
              filterSceneName={selectedSceneName}
              onClearFilter={() => {
                sceneSelectedFromGraphRef.current = false;
                setSelectedSceneId(null);
                setSelectedSceneName(null);
              }}
              onSceneClick={(contentId, sceneName) => {
                sceneSelectedFromGraphRef.current = false;
                // contentId from API (content.parent.id) is the same as contentId used in graph
                setSelectedSceneId(contentId);
                setSelectedSceneName(sceneName);
              }}
              onInteractionClick={(contentId, contentName, contentType) => {
                setSelectedInteraction({ contentId, contentName, contentType });
              }}
            />
          </div>
        </div>
      )}

      {/* Sessions Tab Content */}
      {activeTab === "sessions" && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Search, Sort and Export */}
          <div className="flex items-center justify-between gap-4 mb-3">
            <SearchAndSort
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              searchPlaceholder={t("statsNew.session.searchPlaceholder")}
              sortOptions={sortOptions}
              selectedSort={sortKey}
              onSortChange={setSortKey}
            />
            <ExportButton onExport={handleExport} disabled={displaySessions.length === 0} />
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {filterChips.map((chip) => (
              <button
                key={chip.id}
                onClick={() => toggleFilter(chip.id)}
                className={`filter-chip ${activeFilters.includes(chip.id) ? "active" : ""}`}
              >
                {chip.label}
              </button>
            ))}
            {activeFilters.length > 0 && (
              <button
                onClick={() => setActiveFilters([])}
                className="text-sm text-gray-500 hover:text-gray-700 ml-2 underline"
              >
                {t("statsNew.filters.clearAll")}
              </button>
            )}
          </div>

          {/* Results counter */}
          <p className="text-sm text-gray-500 mb-3">
            {displaySessions.length} session{displaySessions.length !== 1 ? "s" : ""}{" "}
            {t("statsNew.filters.found")}
          </p>

          {/* Sessions list */}
          {displaySessions.length === 0 ? (
            searchValue ? (
              <EmptyState
                icon="search"
                title={t("statsNew.emptyState.noSearchResults")}
                description={t("statsNew.emptyState.noSearchResultsDescription")}
              />
            ) : dateRange.startDate || dateRange.endDate ? (
              <EmptyState
                icon="calendar"
                title={t("statsNew.emptyState.noSessions")}
                description={t("statsNew.emptyState.noSessionsDescription")}
                action={{
                  label: t("statsNew.emptyState.viewAll"),
                  onClick: handleClearDateRange,
                }}
              />
            ) : (
              <EmptyState
                icon="session"
                title={t("statsNew.emptyState.noSessions")}
                description={t("statsNew.emptyState.noSessionsDescription")}
              />
            )
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                {paginatedSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    id={session.id}
                    date={getSessionDate(session)}
                    score={session.score ?? null}
                    maxScore={session.score_max ?? null}
                    duration={session.total_session_duration ?? null}
                    progressionPercent={session.total_session_completion_percentage ?? null}
                    isCompleted={session.is_completed || false}
                    isSucceeded={session.is_succeeded || false}
                    userName={session.user_name || session.enduser?.username}
                    userAvatarUrl={session.enduser?.source_avatar?.s3_url}
                    onClick={() => setSelectedSessionId(session.id)}
                    averageScore={moduleStats.avgScore}
                    hasGazeData={gazeDataAvailability[session.id] ?? false}
                  />
                ))}
              </div>
              <div className="flex-shrink-0 mt-auto">
                <Pagination
                  currentPage={currentPage}
                  totalItems={displaySessions.length}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Apprenants Tab Content */}
      {activeTab === "apprenants" && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Search and Sort */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <SearchAndSort
              searchValue={apprenantsSearchValue}
              onSearchChange={setApprenantsSearchValue}
              searchPlaceholder={t("statsNew.apprenants.searchPlaceholder")}
              sortOptions={apprenantsSortOptions}
              selectedSort={apprenantsSortKey}
              onSortChange={setApprenantsSortKey}
            />
          </div>

          {/* Results counter */}
          <p className="text-sm text-gray-500 mb-3">
            {displayLearners.length} {t("statsNew.module.uniqueLearners")}
          </p>

          {/* Learners table */}
          {displayLearners.length === 0 ? (
            apprenantsSearchValue ? (
              <EmptyState
                icon="search"
                title={t("statsNew.emptyState.noSearchResults")}
                description={t("statsNew.emptyState.noSearchResultsDescription")}
              />
            ) : (
              <EmptyState
                icon="users"
                title={t("statsNew.emptyState.noApprenants")}
                description={t("statsNew.emptyState.noApprenantsDescription")}
              />
            )
          ) : (
            <>
              <div className="flex-1 overflow-y-auto min-h-0 bg-white rounded-xl border border-gray-200">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>{t("statsNew.apprenants.sessions")}</th>
                      <th></th>
                      <th>{t("statsNew.apprenants.firstName")}</th>
                      <th>{t("statsNew.apprenants.lastName")}</th>
                      <th>{t("statsNew.apprenants.email")}</th>
                      <th>{t("statsNew.module.bestScore")}</th>
                      <th>{t("statsNew.apprenants.lastSession")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLearners.map((learner) => {
                      const isSimplified = learner.email?.endsWith("@sneakysneaky.ua");
                      return (
                        <tr
                          key={learner.isAnonymous ? `anon_${learner.id}` : learner.id}
                          className="clickable"
                          onClick={() => {
                            if (learner.isAnonymous) {
                              navigate("/statistics/apprenants/anonymous");
                            } else {
                              navigate(`/statistics/apprenants/${learner.id}`);
                            }
                          }}
                        >
                          <td>
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              {learner.sessionCount}
                            </span>
                          </td>
                          <td>
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                              {learner.avatarUrl ? (
                                <img
                                  src={learner.avatarUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-6 w-6 text-gray-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              )}
                            </div>
                          </td>
                          <td className="font-medium">
                            {learner.isAnonymous ? (
                              <span className="text-gray-400 italic">
                                {learner.username || t("statsNew.apprenants.anonymous")}
                              </span>
                            ) : (
                              learner.firstName || "-"
                            )}
                          </td>
                          <td>{learner.isAnonymous ? "-" : learner.lastName || "-"}</td>
                          <td className="text-gray-500">
                            {learner.isAnonymous ? (
                              "-"
                            ) : !learner.email ? (
                              "-"
                            ) : isSimplified ? (
                              <div className="flex items-center gap-2">
                                <span className="font-mono">
                                  {parseSimplifiedUserIdentifier(learner.email)}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  {t("statsNew.apprenants.simplified")}
                                </span>
                              </div>
                            ) : (
                              learner.email
                            )}
                          </td>
                          <td>
                            <span
                              className={`font-semibold ${
                                learner.bestScore != null
                                  ? learner.bestScore >= 16
                                    ? "text-green-600"
                                    : learner.bestScore >= 10
                                    ? "text-yellow-600"
                                    : "text-red-600"
                                  : "text-gray-400"
                              }`}
                            >
                              {learner.bestScore != null ? learner.bestScore.toFixed(1) : "-"}
                            </span>
                          </td>
                          <td className="text-gray-500">
                            {learner.lastSessionDate
                              ? format(learner.lastSessionDate, "dd MMM yyyy", { locale })
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex-shrink-0 mt-auto">
                <Pagination
                  currentPage={apprenantsCurrentPage}
                  totalItems={displayLearners.length}
                  pageSize={apprenantsPageSize}
                  onPageChange={setApprenantsCurrentPage}
                  onPageSizeChange={(newSize) => {
                    setApprenantsPageSize(newSize);
                    setApprenantsCurrentPage(0);
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSessionId && (
        <SessionDetailModal
          sessionId={selectedSessionId}
          onClose={() => {
            setSelectedSessionId(null);
            // Remove sessionId from URL when closing modal
            if (searchParams.has("sessionId")) {
              searchParams.delete("sessionId");
              setSearchParams(searchParams, { replace: true });
            }
          }}
        />
      )}

      {/* Interaction Attempts Modal */}
      {selectedInteraction && (
        <InteractionAttemptsModal
          contentId={selectedInteraction.contentId}
          contentName={selectedInteraction.contentName}
          contentType={selectedInteraction.contentType}
          projectId={Number(moduleId)}
          startDate={dateRange.startDate?.toISOString()}
          endDate={dateRange.endDate?.toISOString()}
          onClose={() => setSelectedInteraction(null)}
        />
      )}
    </div>
  );
};

export default ModuleView;
